import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { IItem } from '@rbi-ctg/menu';
import { ItemAvailabilityStatus } from 'enums/menu';
import useEffectOnce from 'hooks/use-effect-once';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { useFeatureDonation } from 'hooks/use-feature-donation';
import { CustomEventNames, EventTypes, logRBIEvent } from 'state/amplitude';
import { useMenuContext } from 'state/menu';
import useDonation from 'state/order/hooks/use-donation';
import { useUIContext } from 'state/ui';
import { logger } from 'utils/logger';

import { DonationAtCheckout } from '../donation-at-checkout';
import { IDonationAtCheckoutProps } from '../donation-at-checkout/types';

interface IDonationContainerProps {
  isLoading: boolean;
}

const useDonationItems = () => {
  const { featureDonation, loading } = useFeatureDonation();
  const { getOneMenuItemAvailability, checkItemAvailability, pricingFunction } = useMenuContext();

  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const [donationItems, setDonationItems] = useState<IItem[]>([]);

  useEffect(() => {
    if (isUnifiedMenuEnabled) {
      const items =
        featureDonation?.donationItemList?.filter(
          item =>
            getOneMenuItemAvailability(item._id).availabilityStatus ===
            ItemAvailabilityStatus.AVAILABLE
        ) ?? [];
      setDonationItems(items as IItem[]);
    } else {
      (async () => {
        try {
          const list = featureDonation?.donationItemList ?? [];
          const availabilityFlags = await Promise.all(
            list.map(async item => {
              // in order to be able to use the same availability check function for
              // legacy menu, we need to add the 'item-' prefix to the item id
              const itemId = !item._id.includes('item-') ? `item-${item._id}` : item._id;
              const availabilityStatus = (await checkItemAvailability(itemId)).availabilityStatus;
              return availabilityStatus === ItemAvailabilityStatus.AVAILABLE;
            })
          );
          const items = list.filter((item, idx) => {
            if (!availabilityFlags[idx]) {
              return false;
            }
            const price = pricingFunction({ item: (item as unknown) as IItem });
            return price > 0;
          });
          setDonationItems((items as unknown) as IItem[]);
        } catch (error) {
          logger.error({
            error,
            message: 'Error checking donation items availability',
            context: { featureDonation },
          });
        }
      })();
    }
  }, [
    checkItemAvailability,
    featureDonation,
    featureDonation?.donationItemList,
    getOneMenuItemAvailability,
    isUnifiedMenuEnabled,
    pricingFunction,
  ]);

  return { donationItems, loading };
};

const DonationContainer = ({ isLoading }: IDonationContainerProps) => {
  const { featureDonation } = useFeatureDonation();
  const { pricingFunction } = useMenuContext();
  const { formatCurrencyForLocale } = useUIContext();
  const { donationItems, loading } = useDonationItems();

  const {
    handleAddDonationCartEntry,
    handleRemoveDonationCartEntry,
    selectedDonationCartEntry,
  } = useDonation({
    skipRemove: loading,
    donationItems,
  });

  const allDonationOptions: IDonationAtCheckoutProps['donationOptions'] = useMemo(
    () => [
      ...(donationItems.map(item => ({
        label: formatCurrencyForLocale(pricingFunction({ item })),
        value: item._id,
        isDisabled: false,
      })) as IDonationAtCheckoutProps['donationOptions']),
    ],
    [donationItems, formatCurrencyForLocale, pricingFunction]
  );

  const handleDonationToggleChange = useCallback(
    (newSelectedDonationId: string) => {
      if (selectedDonationCartEntry?._id === newSelectedDonationId) {
        handleRemoveDonationCartEntry({ cartId: selectedDonationCartEntry.cartId });
        return;
      }

      const optionItem = donationItems.find(item => item?._id === newSelectedDonationId);
      if (!optionItem) {
        return;
      }
      handleAddDonationCartEntry(optionItem);
    },
    [
      donationItems,
      handleAddDonationCartEntry,
      handleRemoveDonationCartEntry,
      selectedDonationCartEntry,
    ]
  );

  const haveAllItemsZeroPrice = (donationItems: IItem[]): boolean => {
    const total = donationItems.reduce(
      (accumulator, currentItem) => accumulator + pricingFunction({ item: currentItem }),
      0
    );

    return total <= 0;
  };

  useEffectOnce(() => {
    if (donationItems.length) {
      logRBIEvent({
        name: CustomEventNames.FOUNDATION_DONATIONS_VIEW,
        type: EventTypes.Other,
        attributes: {},
      });
    }
  });

  if (!donationItems.length || haveAllItemsZeroPrice(donationItems)) {
    return null;
  }

  return (
    <DonationAtCheckout
      isLoading={isLoading}
      header={featureDonation?.header?.locale ?? ''}
      subtitle={featureDonation?.subtitle?.locale ?? ''}
      donationOptions={allDonationOptions}
      selectedDonationId={selectedDonationCartEntry?._id}
      onChangeValue={handleDonationToggleChange}
    />
  );
};

export default DonationContainer;
